import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
  User,
  Box,
  Activity,
  Settings,
  Truck,
  LogOut,
  ChevronRight,
  Bell,
  HelpCircle,
  Sparkles,
  CreditCard,
  Package,
  MapPin,
  Clock,
  Star,
} from "lucide-react";
import { clearOrders } from "../../redux/ordersSlice";
import { clearCart } from "../../redux/cartSlice";

const menuItems = [
  { icon: User, label: "View Profile", path: "/__profile" },
  { icon: Box, label: "Products/Services Directory", path: "/_products_services_directories" },
  { icon: Activity, label: "Recent Activity", path: "/_your-recent-activity74924679248598243920000000000000000000000000000000000" },
  { icon: Settings, label: "Settings", badge: "NEW", path: "/__settings_" },
  { icon: Truck, label: "Ship With UNIQUE ELEVATORS", sub: "Easy booking of transport", path: "/shipwithue" },
];

const ProfileDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [notifications] = useState(3); // Example notification count

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Load User from LocalStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const handleClick = (item) => {
    if (item.path) {
      navigate(item.path);
      setIsOpen(false);
    }
  };

  const clearAuthData = () => {
    // Clear all local storage items
    localStorage.removeItem("user");
    localStorage.removeItem("role");
    localStorage.removeItem("vendorId");
    
    // Clear all cookies
    const cookies = document.cookie.split(";");
    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;domain=" + window.location.hostname + ";path=/";
    });

    // Clear session storage
    sessionStorage.clear();

    // Clear Redux state
    dispatch(clearOrders());
    dispatch(clearCart());
  };

  const handleLogout = () => {
    clearAuthData();
    toast.success("Logged out successfully!");
    navigate("/login");
    setIsOpen(false);
  };

  const phoneNumber = user?.phone || "";

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {/* Trigger Button */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-all"
      >
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
          <User size={16} className="text-white" />
        </div>
        <span className="hidden md:inline font-medium text-sm">Profile</span>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-3 w-80 bg-white rounded-2xl shadow-2xl overflow-hidden z-50 border border-gray-100"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-5 text-white relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-32 h-32 bg-white/10 rounded-full" />
              <div className="absolute -right-5 -bottom-5 w-20 h-20 bg-white/10 rounded-full" />
              
              <div className="relative flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center ring-2 ring-white/30">
                  <User size={28} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Welcome Back!</h3>
                  <p className="text-white/80 text-sm">+91 {phoneNumber}</p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-2 p-4 bg-gray-50 border-b border-gray-100">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="text-center p-3 bg-white rounded-xl shadow-sm cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center mx-auto mb-1">
                  <Package size={16} />
                </div>
                <p className="text-xs font-semibold text-gray-800">12</p>
                <p className="text-[10px] text-gray-500">Orders</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="text-center p-3 bg-white rounded-xl shadow-sm cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-1">
                  <Star size={16} />
                </div>
                <p className="text-xs font-semibold text-gray-800">4.8</p>
                <p className="text-[10px] text-gray-500">Rating</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="text-center p-3 bg-white rounded-xl shadow-sm cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center mx-auto mb-1">
                  <CreditCard size={16} />
                </div>
                <p className="text-xs font-semibold text-gray-800">₹500</p>
                <p className="text-[10px] text-gray-500">Wallet</p>
              </motion.div>
            </div>

            {/* Menu Items */}
            <ul className="py-2">
              {menuItems.map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <button
                    onClick={() => handleClick(item)}
                    className="w-full px-5 py-3 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gray-100 text-gray-500 group-hover:bg-gradient-to-br group-hover:from-blue-500 group-hover:to-indigo-600 group-hover:text-white transition-all flex items-center justify-center shadow-sm">
                      <item.icon size={18} />
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-gray-800 block group-hover:text-blue-600 transition-colors">
                        {item.label}
                      </span>
                      {item.sub && (
                        <p className="text-xs text-gray-500">{item.sub}</p>
                      )}
                    </div>
                    {item.badge && (
                      <span className="px-2 py-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[10px] font-semibold rounded-full shadow-sm">
                        {item.badge}
                      </span>
                    )}
                    <ChevronRight size={16} className="text-gray-300 group-hover:text-blue-600 transition-colors" />
                  </button>
                </motion.li>
              ))}
            </ul>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <HelpCircle size={16} />
                  <span>Need Help?</span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-xl shadow-lg shadow-blue-500/30"
                >
                  Contact Support
                </motion.button>
              </div>
            </div>

            {/* Logout */}
            <div className="p-3 border-t border-gray-100">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-red-500 hover:bg-red-50 rounded-xl transition-colors font-medium"
              >
                <LogOut size={18} />
                Logout
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfileDropdown;
